import React, { useEffect, useState } from "react";
import { Button, Group, Text, TextInput, Modal } from "@mantine/core";
import { useEmailStore } from "@components/Tax Pro Only/Modals/EmailModal/emailStore.ts";
import { notifications } from "@mantine/notifications";
import {
  NEW_TEMPLATE_ID,
  NEW_TEMPLATE_NAME,
} from "@components/Tax Pro Only/Modals/EmailModal/constants.ts";
import "./EmailTemplate.scss";
import type { Template } from "@components/Tax Pro Only/Modals/EmailModal/types.ts";
import TemplateDropdownContent from "./TemplateDropdownContent";

// Define the constant locally since it's not exported from constants.ts
const TEMPLATE_BODY_PLACEHOLDER = "Enter your email content here.";

/**
 * EmailTemplate component allows users to select, create, update, and delete email templates.
 */
const EmailTemplate: React.FC = () => {
  // Local state
  const [selectedTemplateName, setSelectedTemplateName] =
    useState<string>(NEW_TEMPLATE_NAME);
  const [newTemplateName, setNewTemplateName] = useState<string>("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [isDefaultTemplate, setIsDefaultTemplate] = useState<boolean>(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState<boolean>(false);
  const [isSavingAsNew, setIsSavingAsNew] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [defaultTemplatesCollapsed, setDefaultTemplatesCollapsed] =
    useState<boolean>(false);
  const [customTemplatesCollapsed, setCustomTemplatesCollapsed] =
    useState<boolean>(false);
  const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  // Email Store Properties
  const {
    setBody,
    setSubject,
    templates,
    saveTemplate,
    updateTemplate,
    loadTemplates,
    deleteTemplate,
  } = useEmailStore();

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  /**
   * Resets the template fields to their default values.
   */
  const resetTemplateFields = (): void => {
    setBody(TEMPLATE_BODY_PLACEHOLDER || "");
    setSubject("");
    setSelectedTemplateId(null);
    setIsDefaultTemplate(false);
  };

  /**
   * Sets the fields based on the selected template.
   */
  const setTemplateFields = (template: Template): void => {
    if (!template) {
      resetTemplateFields();
      return;
    }

    setBody(template.body || "");
    setSubject(template.subject || "");
    setSelectedTemplateId(template.id || null);
    setIsDefaultTemplate(!!template.isDefault);
  };

  /**
   * Handles the selection of a template from the dropdown.
   */
  const onSelectTemplate = (templateName: string): void => {
    try {
      if (!templateName) {
        console.error("No template value selected");
        return;
      }

      setSelectedTemplateName(templateName);
      setDropdownOpen(false);

      if (templateName === NEW_TEMPLATE_NAME) {
        // User selected "Add a template"
        setNewTemplateName("");
        setSelectedTemplateId(null);
        setIsCreatingTemplate(true);
        setIsSavingAsNew(false);
        setIsDefaultTemplate(false);
        resetTemplateFields();
      } else {
        // User selected an existing template
        setIsCreatingTemplate(false);
        setIsSavingAsNew(false);

        if (!templates || !Array.isArray(templates)) {
          console.error("Templates is not available");
          return;
        }

        // Find the selected template
        const selectedTemplate = templates.find((t) => t.name === templateName);

        if (!selectedTemplate) {
          console.error("Selected template not found:", templateName);
          return;
        }

        setTemplateFields(selectedTemplate);
      }
    } catch (error) {
      console.error("Error in onSelectTemplate:", error);
      notifications.show({
        message: "Error selecting template. Please try again.",
        color: "red",
      });
    }
  };

  /**
   * Handles saving a new template.
   */
  const handleSaveTemplate = async (): Promise<void> => {
    try {
      if (!newTemplateName) {
        notifications.show({
          message: "Please enter a template name.",
          color: "red",
        });
        return;
      }

      const savedTemplate = await saveTemplate(newTemplateName);
      if (savedTemplate) {
        setSelectedTemplateName(savedTemplate.name);
        setSelectedTemplateId(savedTemplate.id);
        setIsCreatingTemplate(false);
        setIsSavingAsNew(false);
      } else {
        console.error("Template saved but returned null or undefined");
      }
    } catch (error) {
      console.error("Error saving template:", error);
    }
  };

  /**
   * Opens the update confirmation modal.
   */
  const openUpdateModal = (): void => {
    if (isDefaultTemplate || selectedTemplateId === NEW_TEMPLATE_ID) {
      return;
    }
    setUpdateModalOpen(true);
  };

  /**
   * Handles updating an existing template after confirmation.
   */
  const confirmUpdateTemplate = async (): Promise<void> => {
    try {
      if (!selectedTemplateId) {
        notifications.show({
          message: "No template selected to update.",
          color: "red",
        });
        return;
      }

      if (isDefaultTemplate) {
        notifications.show({
          message: "Default templates cannot be updated.",
          color: "red",
        });
        return;
      }

      await updateTemplate(selectedTemplateId);
      setUpdateModalOpen(false);
    } catch (error) {
      console.error("Error updating template:", error);
    }
  };

  /**
   * Handles saving the current template as a new template.
   */
  const handleSaveAsNewTemplate = (): void => {
    setIsCreatingTemplate(false);
    setIsSavingAsNew(true);
    setNewTemplateName("");
  };

  /**
   * Opens the delete confirmation modal.
   */
  const openDeleteModal = (): void => {
    if (isDefaultTemplate || selectedTemplateId === NEW_TEMPLATE_ID) {
      return;
    }
    setDeleteModalOpen(true);
  };

  /**
   * Handles deleting an existing template after confirmation.
   */
  const confirmDeleteTemplate = async (): Promise<void> => {
    try {
      if (!selectedTemplateId) {
        notifications.show({
          message: "You must select a template to delete it.",
          color: "red",
        });
        return;
      }

      if (selectedTemplateId === NEW_TEMPLATE_ID) {
        notifications.show({
          message: "Please select a template.",
        });
        return;
      }

      if (isDefaultTemplate) {
        notifications.show({
          message: "Default templates cannot be deleted.",
          color: "red",
        });
        return;
      }

      await deleteTemplate(selectedTemplateId);
      setDeleteModalOpen(false);
      setSelectedTemplateName(NEW_TEMPLATE_NAME);
      resetTemplateFields();
    } catch (error) {
      console.error("Error deleting template:", error);
      notifications.show({
        message: "Error deleting template. Please try again.",
        color: "red",
      });
    }
  };

  // Group templates by type
  const defaultTemplates = Array.isArray(templates)
    ? templates.filter((t) => t.isDefault)
    : [];

  const customTemplates = Array.isArray(templates)
    ? templates.filter((t) => !t.isDefault)
    : [];

  // Toggle default templates section
  const toggleDefaultTemplates = (
    e: React.MouseEvent | React.KeyboardEvent
  ) => {
    e.stopPropagation();
    setDefaultTemplatesCollapsed(!defaultTemplatesCollapsed);
  };

  // Toggle custom templates section
  const toggleCustomTemplates = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setCustomTemplatesCollapsed(!customTemplatesCollapsed);
  };

  // Handle keyboard events for interactive elements
  const handleKeyDown = (
    e: React.KeyboardEvent,
    callback: (e: React.KeyboardEvent) => void
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback(e);
    }
  };

  return (
    <div className="email-template-container">
      <Text className="email-template-description" size="sm">
        Choose a pre-configured template to use for sending your email or create
        a new one.
      </Text>

      {/* Template selection */}
      <Text size="md" fw={600} mb={5}>
        Select Template
      </Text>

      {/* Custom dropdown instead of Mantine Select */}
      <div className="custom-dropdown">
        <button
          className="dropdown-header"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          onKeyDown={(e) =>
            handleKeyDown(e, () => setDropdownOpen(!dropdownOpen))
          }
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
        >
          <span>{selectedTemplateName}</span>{" "}
          <span className="dropdown-arrow" aria-hidden="true">
            {dropdownOpen ? "▲" : "▼"}
          </span>
        </button>

        {dropdownOpen && (
          <TemplateDropdownContent
            selectedTemplateName={selectedTemplateName}
            defaultTemplates={defaultTemplates}
            customTemplates={customTemplates}
            defaultTemplatesCollapsed={defaultTemplatesCollapsed}
            customTemplatesCollapsed={customTemplatesCollapsed}
            toggleDefaultTemplates={toggleDefaultTemplates}
            toggleCustomTemplates={toggleCustomTemplates}
            onSelectTemplate={onSelectTemplate}
            handleKeyDown={handleKeyDown}
          />
        )}
      </div>

      {(isCreatingTemplate || isSavingAsNew) && (
        <TextInput
          placeholder="Template Name"
          value={newTemplateName}
          onChange={(e) => setNewTemplateName(e.target.value)}
          mt={10}
        />
      )}

      <Group>
        {isCreatingTemplate ? (
          <Button onClick={handleSaveTemplate}>Save Template</Button>
        ) : (
          <>
            <Button
              onClick={openUpdateModal}
              disabled={
                isDefaultTemplate ?? selectedTemplateId === NEW_TEMPLATE_ID
              }
            >
              Update Template
            </Button>
            {isSavingAsNew ? (
              <Button
                onClick={handleSaveTemplate}
                disabled={!newTemplateName.trim()}
              >
                {newTemplateName.trim()
                  ? "Confirm Template Name"
                  : "Save as New Template"}
              </Button>
            ) : (
              <Button
                onClick={handleSaveAsNewTemplate}
                disabled={selectedTemplateId === NEW_TEMPLATE_ID}
              >
                Save as New Template
              </Button>
            )}
            <Button
              onClick={openDeleteModal}
              disabled={
                isDefaultTemplate ?? selectedTemplateId === NEW_TEMPLATE_ID
              }
              color="red"
            >
              Delete Template
            </Button>
          </>
        )}
      </Group>

      {/* Update Template Confirmation Modal */}
      <Modal
        opened={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        title="Confirm Template Update"
        centered
      >
        <Text mb={20}>
          Are you sure you want to update the template "{selectedTemplateName}"?
          This action cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="outline" onClick={() => setUpdateModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmUpdateTemplate}>Update Template</Button>
        </Group>
      </Modal>

      {/* Delete Template Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Template Deletion"
        centered
      >
        <Text mb={20}>
          Are you sure you want to delete the template "{selectedTemplateName}"?
          This action cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={confirmDeleteTemplate}>
            Delete Template
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default EmailTemplate;
